import { SESSION_STATUS } from '@/constants/auth';
import { useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { createApiClients } from '@/lib/createApiClients';
import { PrivateContextData } from '@/components/private/PrivateContext';
import { RoleUserOrgAssociationDto } from '@/apiClient';

export function useInitializeContext(
  selectedOrganisationId?: number,
  userOrganisations?: PrivateContextData['userOrganisations'],
  setContextData?: (
    data:
      | PrivateContextData
      | ((prev: PrivateContextData) => PrivateContextData),
  ) => void,
  setUserOrganisations?: (
    orgs: RoleUserOrgAssociationDto[] | undefined,
  ) => void,
  setSelectedOrganisationId?: (id: number | undefined) => void,
) {
  const { data: session, status } = useSession();
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!setContextData) return;
    if (status !== SESSION_STATUS.AUTHENTICATED || !session?.user) return;
    if (hasInitializedRef.current) return;

    hasInitializedRef.current = true;

    const { axiosInstance, userApi, userProfileApi, ...apiClients } =
      createApiClients();

    // Merge with `prev` rather than replacing — `PrivateStateProvider`
    // publishes `setContextData` and the other setters into the context
    // value, and a hard replace here would wipe them out and break later
    // updates (e.g. `Private`'s userProfile fold).
    setContextData((prev) => ({
      ...prev,
      axiosInstance,
      userId: session.user.internalUserId,
      userGuid: session.user.userGuid,
      userEmail: session.user.email ?? undefined,
      loginFlowComplete: true,
      userApi,
      userProfileApi,
      selectedOrganisationId,
      setSelectedOrganisationId,
      setUserOrganisations,
      userOrganisations,
      ...apiClients,
    }));
  }, [
    session,
    status,
    selectedOrganisationId,
    userOrganisations,
    setContextData,
    setUserOrganisations,
    setSelectedOrganisationId,
  ]);
}
